if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.needsCartReview || this.submitButton.disabled || this.variantIdInput.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const quantityInput = this.form.elements.namedItem('quantity');
        if (quantityInput && typeof quantityInput.checkValidity === 'function') {
          quantityInput.setCustomValidity('');
          const value = Number(quantityInput.value);
          if (!quantityInput.value.trim() || !Number.isSafeInteger(value) || value <= 0) {
            quantityInput.setCustomValidity(window.variantStrings?.invalidQuantity || 'Choose a valid quantity.');
          }
          if (!quantityInput.checkValidity()) {
            quantityInput.reportValidity();
            return;
          }
        }
        this.handleErrorMessage();
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        let itemAdded = false;
        let requestStarted = false;
        Promise.resolve().then(() => {
          const pending = fetch(`${routes.cart_add_url}`, config);
          requestStarted = true;
          return pending;
        })
          .then(async (response) => {
            const data = await response.json();
            if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid cart response');
            if (data.status) return data;
            if (response.ok === false) throw new Error('Cart request failed');
            const validItem = (item) => item && Number.isSafeInteger(Number(item.id)) && Number(item.id) > 0;
            if (!validItem(data) && !(Array.isArray(data.items) && data.items.length && data.items.every(validItem))) {
              throw new Error('Missing added item');
            }
            return data;
          })
          .then(async (response) => {
            itemAdded = !response.status;
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage || this.variantIdInput.value !== String(variantId)) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              if (window.asenmoCartFeedback?.()) {
                await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: response });
              } else window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);
            this.cart.classList.remove('is-empty');

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              await publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.needsCartReview = itemAdded || requestStarted;
            this.error = this.needsCartReview;
            this.handleErrorMessage(itemAdded
              ? (window.cartStrings?.addedButCartFailed || 'Item added. Open or refresh your cart before adding it again.')
              : this.needsCartReview
                ? (window.cartStrings?.addUnconfirmed || 'We could not confirm whether this item was added. Review your cart before adding it again.')
                : (window.cartStrings?.error || 'Unable to add this item. Please try again.'));
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors && !this.needsCartReview) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        // Compact product cards have no pre-rendered error region.
        if (!this.errorMessageWrapper && errorMessage) {
          this.errorMessageWrapper = document.createElement('div');
          this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
          this.errorMessageWrapper.setAttribute('role', 'alert');
          this.errorMessage = document.createElement('span');
          this.errorMessage.className = 'product-form__error-message';
          this.errorMessageWrapper.append(this.errorMessage);
          this.form.before(this.errorMessageWrapper);
        }
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          if (this.needsCartReview && !this.errorMessageWrapper.querySelector('[data-purchase-cart-review]')) {
            const review = document.createElement('a');
            review.href = window.routes?.cart_url || '/cart';
            review.dataset.purchaseCartReview = '';
            review.className = 'link underlined-link';
            review.textContent = window.cartStrings?.reviewCart || 'Review your cart';
            this.errorMessageWrapper.append(document.createTextNode(' '), review);
          }
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) {
            this.submitButtonText.textContent = text;
            const price = this.submitButton.querySelector('[data-atc-button-price]');
            if (price) price.hidden = true;
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          if (!this.submitButton.classList.contains('loading') && !this.needsCartReview) this.submitButton.removeAttribute('aria-disabled');
          this.error = this.needsCartReview === true;
          this.submitButtonText.classList.remove('hidden');
          this.submitButton.querySelector('.sold-out-message')?.classList.add('hidden');
          this.submitButtonText.textContent = text || this.dataset.addToCartLabel || window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
